import { serverDate } from '../../../shared/helpers/dates';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ImageLightboxComponent } from '../../../shared/components/ui/image-lightbox/image-lightbox.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { UserCellComponent } from '../../../shared/components/ui/user-cell/user-cell.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { VehiclesService } from '../../../shared/services/vehicles.service';
import {
  OPTION_CATEGORY_LABEL,
  OPTION_CATEGORY_ORDER,
  OptionCategory,
  VehicleOption,
} from '../../../shared/types/option.types';
import {
  FUEL_TYPE_LABEL,
  GEARBOX_LABEL,
  VEHICLE_STATUS_META,
  Vehicle,
} from '../../../shared/types/vehicle.types';

@Component({
  selector: 'app-vehicle-detail',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    LocalizedDatePipe,
    DecimalPipe,
    PageHeaderComponent,
    DataStateComponent,
    ModalComponent,
    ConfirmModalComponent,
    UserCellComponent,
    ImageLightboxComponent,
  ],
  templateUrl: './vehicle-detail.component.html',
})
export class VehicleDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly vehiclesService = inject(VehiclesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly vehicle = signal<Vehicle | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  /** URL affichée en grand dans la lightbox (null = fermée). */
  readonly lightboxSrc = signal<string | null>(null);

  // approve modal
  readonly approveOpen = signal(false);
  readonly approving = signal(false);
  readonly approveError = signal<string | null>(null);

  // reject modal
  readonly rejectOpen = signal(false);
  readonly rejecting = signal(false);
  readonly rejectError = signal<string | null>(null);
  readonly rejectReasonCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3)],
  });

  // delete modal
  readonly deleteOpen = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  // featured (curation « à la une »)
  readonly featuring = signal(false);
  readonly featureError = signal<string | null>(null);
  readonly isFeatured = computed(() => this.vehicle()?.featured === true);

  readonly statusMeta = VEHICLE_STATUS_META;
  readonly fuelLabel = FUEL_TYPE_LABEL;
  readonly gearLabel = GEARBOX_LABEL;
  readonly catLabel = OPTION_CATEGORY_LABEL;

  /** Équipements regroupés par catégorie (ordre stable de OPTION_CATEGORY_ORDER).
   *  Affiché en read-only sur la fiche admin — la mutation est côté owner. */
  readonly optionsByCategory = computed<{ category: OptionCategory; label: string; options: VehicleOption[] }[]>(() => {
    const list = this.vehicle()?.options ?? [];
    if (!list.length) return [];
    const buckets = new Map<OptionCategory, VehicleOption[]>();
    for (const opt of list) {
      const cat = (opt.category ?? 'AUTRE') as OptionCategory;
      const bucket = buckets.get(cat);
      if (bucket) bucket.push(opt);
      else buckets.set(cat, [opt]);
    }
    const out: { category: OptionCategory; label: string; options: VehicleOption[] }[] = [];
    for (const cat of OPTION_CATEGORY_ORDER) {
      const opts = buckets.get(cat);
      if (opts?.length) {
        opts.sort((a, b) => a.nameFr.localeCompare(b.nameFr, 'fr'));
        out.push({ category: cat, label: OPTION_CATEGORY_LABEL[cat], options: opts });
      }
    }
    return out;
  });

  readonly displayName = computed(() => {
    const v = this.vehicle();
    if (!v) return '';
    const make = v.make?.entityCode ?? v.make?.name ?? '';
    const model = v.model?.entityCode ?? v.model?.name ?? '';
    return [make, model].filter(Boolean).join(' ') || `Véhicule #${v.id}`;
  });

  /** Une date ISO est-elle déjà passée ? (colore les dates d'expiration, #55). */
  isExpired(date?: string | null): boolean {
    return date ? serverDate(date).getTime() < Date.now() : false;
  }

  readonly isUnderReview = computed(() => {
    const s = this.vehicle()?.status;
    return s === 'UNDER_REVIEW' || s === 'PRE_UNDER_REVIEW';
  });

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.vehiclesService
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v) => {
          this.vehicle.set(v);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger ce véhicule.'),
          );
        },
      });
  }

  // approve
  openApprove(): void { this.approveError.set(null); this.approveOpen.set(true); }
  closeApprove(): void { this.approveOpen.set(false); }
  confirmApprove(): void {
    this.approving.set(true);
    this.approveError.set(null);
    this.vehiclesService.approve(this.id).subscribe({
      next: (v) => {
        this.approving.set(false);
        this.approveOpen.set(false);
        this.vehicle.set(v);
      },
      error: (err: unknown) => {
        this.approving.set(false);
        this.approveError.set(toErrorMessage(err, "L'approbation a échoué."));
      },
    });
  }

  // reject
  openReject(): void {
    this.rejectError.set(null);
    this.rejectReasonCtrl.reset('');
    this.rejectOpen.set(true);
  }
  closeReject(): void { if (this.rejecting()) return; this.rejectOpen.set(false); }
  confirmReject(): void {
    if (this.rejectReasonCtrl.invalid) {
      this.rejectReasonCtrl.markAsTouched();
      return;
    }
    this.rejecting.set(true);
    this.rejectError.set(null);
    this.vehiclesService.reject(this.id, this.rejectReasonCtrl.value.trim()).subscribe({
      next: (v) => {
        this.rejecting.set(false);
        this.rejectOpen.set(false);
        this.vehicle.set(v);
      },
      error: (err: unknown) => {
        this.rejecting.set(false);
        this.rejectError.set(toErrorMessage(err, 'Le rejet a échoué.'));
      },
    });
  }

  // featured (curation)
  toggleFeatured(): void {
    const v = this.vehicle();
    if (!v || this.featuring()) return;
    const next = !(v.featured ?? false);
    this.featuring.set(true);
    this.featureError.set(null);
    this.vehiclesService
      .setFeatured(this.id, next)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.featuring.set(false);
          this.vehicle.set(updated);
        },
        error: (err: unknown) => {
          this.featuring.set(false);
          this.featureError.set(toErrorMessage(err, "L'opération a échoué."));
        },
      });
  }

  // delete
  openDelete(): void { this.deleteError.set(null); this.deleteOpen.set(true); }
  closeDelete(): void { this.deleteOpen.set(false); }
  confirmDelete(): void {
    this.deleting.set(true);
    this.deleteError.set(null);
    this.vehiclesService.delete(this.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.deleteOpen.set(false);
        void this.router.navigate(['/vehicles']);
      },
      error: (err: unknown) => {
        this.deleting.set(false);
        this.deleteError.set(toErrorMessage(err, 'errors.deleteFailed'));
      },
    });
  }
}
